import { useState, useEffect } from "react";
import {
  CURRENCY_META,
  formatAmount,
  getVisitorPrice,
  getVisitorOriginalPrice,
  type CurrencyCode,
} from "@/config/pricing";

/**
 * Region model:
 *  - "eg"    → Egypt (local pricing in EGP)
 *  - "sa"    → Kept for Arabic-Saudi dialect overrides; billed in USD like every other non-Egypt region
 *  - "other" → International (USD pricing)
 *
 * Currency is decoupled from region: only Egypt gets EGP, everyone else gets USD.
 */
export type Region = "eg" | "sa" | "other";

const REGION_KEY = "detected_region_v2";
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const VALID_REGIONS: Region[] = ["eg", "sa", "other"];

interface CachedRegion {
  region: Region;
  /** epoch ms at which the cache was written */
  ts: number;
  /** "ip" (fetched from geolocation API) or "manual"/"browser" (weaker signal) */
  source: "ip" | "manual" | "browser";
}

/** True only in a real browser. SSR/prerender (Node) has no window/localStorage. */
const isBrowser = typeof window !== "undefined" && typeof localStorage !== "undefined";

/** Neutral region used during SSR/prerender so the server never geolocates. */
const SSR_DEFAULT_REGION: Region = "other";

/** Fire a GA4 event describing where the current region came from. Deduped per source per session. */
type RegionSource = "cache" | "ip" | "browser" | "manual";
function trackRegionSource(source: RegionSource, region: Region, extra?: Record<string, unknown>) {
  if (!isBrowser) return;
  try {
    const sessionKey = `region_src_tracked_${source}`;
    if (sessionStorage.getItem(sessionKey)) return;
    sessionStorage.setItem(sessionKey, "1");
    const w = window as unknown as { gtag?: (...args: unknown[]) => void; dataLayer?: unknown[] };
    const payload = {
      event_category: "region",
      region_source: source,
      region,
      ...extra,
    };
    if (typeof w.gtag === "function") {
      w.gtag("event", "region_detected", payload);
    } else {
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ event: "region_detected", ...payload });
    }
  } catch {}
}

function readCache(): CachedRegion | null {
  if (!isBrowser) return null;
  try {
    const raw = localStorage.getItem(REGION_KEY);
    if (!raw) return null;
    // Legacy shape: plain region string
    if (VALID_REGIONS.includes(raw as Region)) {
      return { region: raw as Region, ts: Date.now(), source: "ip" };
    }
    const parsed = JSON.parse(raw) as CachedRegion;
    if (!VALID_REGIONS.includes(parsed.region)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(entry: CachedRegion) {
  if (!isBrowser) return;
  try { localStorage.setItem(REGION_KEY, JSON.stringify(entry)); } catch {}
}

function isFresh(entry: CachedRegion): boolean {
  return Date.now() - entry.ts < CACHE_TTL_MS;
}

function detectFromBrowser(): Region {
  if (!isBrowser) return SSR_DEFAULT_REGION;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.includes("Cairo") || tz.includes("Africa/Cairo")) return "eg";
    if (tz.includes("Riyadh") || tz.includes("Asia/Riyadh")) return "sa";
    const lang = navigator.language || "";
    if (lang.toLowerCase().startsWith("ar-eg")) return "eg";
    return "other";
  } catch {
    return "other";
  }
}

export function useRegion() {
  const [region, setRegion] = useState<Region>(() => {
    // SSR/prerender: return a neutral default so server + first client render
    // match (no hydration mismatch) and no geolocation API is called.
    if (!isBrowser) return SSR_DEFAULT_REGION;
    const cached = readCache();
    if (cached) return cached.region;
    return detectFromBrowser();
  });
  // Server never fetches — start "loading" only in the browser.
  const [loading, setLoading] = useState(isBrowser);

  useEffect(() => {
    const cached = readCache();

    // Manual override always wins and never expires until user changes it.
    if (cached?.source === "manual") {
      trackRegionSource("manual", cached.region);
      setLoading(false);
      return;
    }

    // Fresh IP-detected cache → skip the network call entirely.
    if (cached?.source === "ip" && isFresh(cached)) {
      trackRegionSource("cache", cached.region, { cache_age_ms: Date.now() - cached.ts });
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const runFetch = () => fetch("https://ipapi.co/json/", { signal: controller.signal })
      .then((r) => r.json())
      .then((data: { country_code?: string }) => {
        const code = (data.country_code || "").toUpperCase();
        let detected: Region = "other";
        if (code === "EG") detected = "eg";
        else if (code === "SA") detected = "sa";
        setRegion(detected);
        writeCache({ region: detected, ts: Date.now(), source: "ip" });
        trackRegionSource("ip", detected, { country_code: code || "unknown" });
      })
      .catch(() => {
        const fallback = detectFromBrowser();
        setRegion(fallback);
        // Store as "browser" with a short-lived timestamp so we retry next visit.
        writeCache({ region: fallback, ts: 0, source: "browser" });
        trackRegionSource("browser", fallback);
      })
      .finally(() => setLoading(false));

    let idleHandle: number | undefined;
    let timeoutHandle: number | undefined;
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (h: number) => void;
    };
    if (typeof w.requestIdleCallback === "function") {
      idleHandle = w.requestIdleCallback(runFetch, { timeout: 3000 });
    } else {
      timeoutHandle = window.setTimeout(runFetch, 1500);
    }

    return () => {
      controller.abort();
      if (idleHandle !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleHandle);
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
  }, []);

  const setManualRegion = (r: Region) => {
    setRegion(r);
    writeCache({ region: r, ts: Date.now(), source: "manual" });
    // Reset dedupe so the manual event fires now even if another source fired earlier.
    try { sessionStorage.removeItem("region_src_tracked_manual"); } catch {}
    trackRegionSource("manual", r);
  };

  return { region, loading, setRegion: setManualRegion };
}

/* ─── Back-compat helpers used by existing components ─── */

export function regionToCurrency(region: Region): CurrencyCode {
  return region === "eg" ? "EGP" : "USD";
}

export const CURRENCY_CONFIG = {
  eg:    { code: "EGP", symbol: CURRENCY_META.EGP.symbol, nameAr: CURRENCY_META.EGP.nameAr, nameEn: CURRENCY_META.EGP.nameEn },
  sa:    { code: "USD", symbol: CURRENCY_META.USD.symbol, nameAr: CURRENCY_META.USD.nameAr, nameEn: CURRENCY_META.USD.nameEn },
  other: { code: "USD", symbol: CURRENCY_META.USD.symbol, nameAr: CURRENCY_META.USD.nameAr, nameEn: CURRENCY_META.USD.nameEn },
} as const;

export function convertPrice(egpAmount: number, region: Region): number {
  return getVisitorPrice(egpAmount, regionToCurrency(region));
}

export function convertOriginalPrice(egpAmount: number, region: Region): number | undefined {
  return getVisitorOriginalPrice(egpAmount, regionToCurrency(region));
}

export function formatPrice(egpAmount: number, region: Region, lang: "ar" | "en"): string {
  const currency = regionToCurrency(region);
  return formatAmount(getVisitorPrice(egpAmount, currency), currency, lang);
}
